"use client";

import { Button } from "@/components/atoms/Button";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitleWithColor } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export interface ApplicationProcessStepRow {
  id?: string | number;
  meta_description?: string;
  meta_priority?: number;
}

export interface ApplicationProcess2Data {
  status?: boolean;
  background_image?: string;
  alt_background_image?: string;
  title?: string;
  title_start_color?: string;
  title_highlight_color?: string;
  subtitle_1?: string;
  subtitle_1_color?: string;
  cta_label?: string;
  accent_color?: string;
  cta_link?: string;
  cta_is_cta?: boolean;
  metadata_2?: ApplicationProcessStepRow[];
}

const FALLBACK_STEPS: ApplicationProcessStepRow[] = [
  {
    meta_description: "Fill your details and complete your application.",
    meta_priority: 1,
  },
  {
    meta_description: "Get shortlisted and Provisional Offer Letter.",
    meta_priority: 2,
  },
  {
    meta_description: "Block your seat and submit the required documents.",
    meta_priority: 3,
  },
  {
    meta_description:
      "Post verification, complete the balance payment to confirm enrolment.",
    meta_priority: 4,
  },
];

interface ApplicationProcess2Props {
  data: ApplicationProcess2Data | { data: ApplicationProcess2Data };
  className?: string;
}

function resolvePayload(
  data: ApplicationProcess2Props["data"],
): ApplicationProcess2Data | undefined {
  const anyData = data as { data?: ApplicationProcess2Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as ApplicationProcess2Data;
  }
  return data as ApplicationProcess2Data;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** Fallback when no image is set — not mixed as an overlay on top of the photo */
const FALLBACK_SECTION_BG = "#1A1A1A";

/** Highlight color for `{...}` in the headline when the CMS has none set. */
const DEFAULT_HIGHLIGHT_COLOR = "#FF6B00";

const CTA_BTN_CLASS =
  "group mt-8 inline-flex h-12 min-w-[160px] items-center justify-center gap-2 rounded-lg px-8 text-[16px] font-semibold text-white shadow-md transition hover:opacity-95 sm:h-[52px] no-underline";

function ApplicationProcess2({ data, className }: ApplicationProcess2Props) {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();

  const {
    status = true,
    background_image,
    alt_background_image = "",
    title = "Our Admission process",
    title_start_color = "#FFFFFF",
    title_highlight_color = DEFAULT_HIGHLIGHT_COLOR,
    subtitle_1 = "",
    subtitle_1_color = "#F3F4F6",
    cta_label = "Apply Now",
    accent_color = "#FF6B00",
    cta_link = "",
    metadata_2 = [],
  } = sectionData || {};

  const rawSection = sectionData as Record<string, unknown> | undefined;
  const ctaIsCta = isCmsShowFlagOn(
    rawSection?.cta_is_cta ?? rawSection?.ctaIsCta,
    true,
  );
  const ctaHrefNormalized = String(cta_link ?? "").trim()
    ? normalizeHref(String(cta_link ?? "").trim())
    : "";
  const primaryOpensLeadPopup = ctaIsCta || !ctaHrefNormalized;

  const sortedSteps = useMemo(() => {
    const rows = metadata_2?.length ? metadata_2 : FALLBACK_STEPS;
    return [...rows].sort(
      (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0),
    );
  }, [metadata_2]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "application_process_2");
  //   }
  // }, [status]);

  const handleApplyLeadPopup = useCallback(() => {
    ctaClick({
      section: "application_process_2",
      component: "admission_steps_apply_now",
      label: cta_label?.trim() || "apply_now",
      step: "1",
      entity: "application_process_2",
      parentEntity: "application_process",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [cta_label, dispatch]);

  if (!status) return null;

  const hasContent =
    !!title || !!subtitle_1 || sortedSteps.length > 0;
  if (!hasContent) return null;

  const hasBgImage = Boolean(background_image?.trim());

  const headlineHtml = formatSectionTitleWithColor(
    title ?? "",
    title_highlight_color,
    DEFAULT_HIGHLIGHT_COLOR,
  );

  return (
    <section
      ref={ref}
      data-application-process="2"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full overflow-hidden",
        "flex justify-center px-5 py-12 sm:px-10 sm:py-16 lg:py-20",
        className,
      )}
      style={{
        ...(!hasBgImage ? { backgroundColor: FALLBACK_SECTION_BG } : {}),
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {hasBgImage ? (
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          <Image
            src={background_image!}
            alt={alt_background_image || ""}
            fill
            className="object-cover object-center"
            sizes="100vw"
            priority={false}
          />
        </div>
      ) : null}

      <div className="relative z-10 mx-auto flex w-full max-w-[640px] flex-col items-center text-center lg:max-w-[720px]">
        {title ? (
          <h2
            className="text-balance text-[32px] font-semibold leading-tight tracking-tight"
            style={{ color: title_start_color }}
            dangerouslySetInnerHTML={{ __html: headlineHtml }}
          />
        ) : null}

        {subtitle_1 ? (
          <p
            className="mt-3 max-w-[560px] text-pretty text-[16px] font-normal leading-relaxed"
            style={{ color: subtitle_1_color }}
          >
            {subtitle_1}
          </p>
        ) : null}

        {primaryOpensLeadPopup ? (
          <Button
            type="button"
            onClick={handleApplyLeadPopup}
            className={CTA_BTN_CLASS}
            style={{ backgroundColor: accent_color }}
          >
            {cta_label}
            <ArrowUpRight
          className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:rotate-[45deg]"
          aria-hidden
        />
          </Button>
        ) : (
          <Link
            href={ctaHrefNormalized}
            className={CTA_BTN_CLASS}
            style={{ backgroundColor: accent_color }}
            onClick={() =>
              ctaClick({
                section: "application_process_2",
                component: "admission_steps_apply_now_link",
                label: cta_label?.trim() || "apply_now",
                step: "1",
                entity: "application_process_2",
                parentEntity: "application_process",
              })
            }
          >
            {cta_label}
            <ArrowUpRight
          className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:rotate-[45deg]"
          aria-hidden
        />
          </Link>
        )}

        <ul className="mt-12 flex w-full max-w-[469px] list-none flex-col gap-6 p-0 sm:gap-7">
          {sortedSteps.map((step, index) => (
            <li key={step.id ?? index}>
              <div
                className={cn(
                  "flex w-full min-h-[74px] items-center gap-3 rounded-full bg-white py-3 pl-3 pr-4 text-left shadow-sm sm:h-[74px] sm:w-[469px] sm:gap-4 sm:py-3.5 sm:pl-4",
                )}
              >
                <div
                  className="flex shrink-0 items-center justify-center rounded-full text-[16px] font-bold text-white"
                  style={{
                    backgroundColor: accent_color,
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    minHeight: 34,
                  }}
                >
                  {index + 1}
                </div>
                <p className="min-w-0 flex-1 text-[16px] font-medium leading-snug text-gray-900">
                  {step.meta_description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default ApplicationProcess2;
